// Old mail from a Google Takeout file on Papers (P5 part 3, decision 148). The owner drops the .mbox file; this page
// reads it in the browser and sends each paper attached to mail of the last two years, a few at a time. Whatever is in
// already is passed over, so the same file dropped again goes on where it stopped.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { ApiError, api } from '../api';
import { useLocale } from '../i18n';
import { mailRef, mailsOf, NotTakeout, type OldMail, readMail } from '../mbox';
import { qk } from '../queryClient';
import { ErrorBox } from './ui';

/** a day short of the server's two years, so a long run never sends what the server would refuse */
const BACK_MS = 729 * 86_400_000;
const BATCH = 25;
const KNOWN_MAX = 200;
const AT_ONCE = 3;
const SEND_TIMEOUT_MS = 120_000;

interface Tally {
  read: number;
  total: number;
  mails: number;
  sent: number;
  known: number;
  old: number;
  junk: number;
  refused: number;
  failed: number;
}
const ZERO: Tally = { read: 0, total: 0, mails: 0, sent: 0, known: 0, old: 0, junk: 0, refused: 0, failed: 0 };

type Item = { mail: OldMail; paper: OldMail['papers'][number] };

export function OldMailCard() {
  const { t } = useLocale();
  const qc = useQueryClient();
  const gmail = useQuery({ queryKey: qk.gmail, queryFn: ({ signal }) => api.gmailStatus(signal) });
  const [state, setState] = useState<'idle' | 'running' | 'done' | 'stopped'>('idle');
  const [tally, setTally] = useState<Tally>(ZERO);
  const [error, setError] = useState<unknown>(null);
  const stop = useRef(false);
  const input = useRef<HTMLInputElement>(null);

  // leaving the page stops the reading (the browser asks first); leaving the screen in the app stops it too
  useEffect(() => {
    if (state !== 'running') return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [state]);
  useEffect(
    () => () => {
      stop.current = true;
    },
    [],
  );

  // a wait that Stop cuts short
  const wait = async (ms: number) => {
    for (let left = ms; left > 0 && !stop.current; left -= 500) await new Promise((r) => setTimeout(r, Math.min(500, left)));
  };

  const run = async (file: File) => {
    stop.current = false;
    setError(null);
    const n: Tally = { ...ZERO, total: file.size };
    setTally(n);
    setState('running');
    let shown = 0;
    const show = (force = false) => {
      if (force || Date.now() - shown > 250) {
        shown = Date.now();
        setTally({ ...n });
      }
    };

    // one paper, sent; a busy server is waited for, a dropped connection tried once more
    const sendOne = async ({ mail, paper }: Item) => {
      for (let attempt = 0; !stop.current; attempt += 1) {
        try {
          const signal = typeof AbortSignal.timeout === 'function' ? AbortSignal.timeout(SEND_TIMEOUT_MS) : undefined;
          const r = await api.addMailPaper({ mailId: mail.gmailId, index: paper.index, receivedAt: mail.receivedAt!.toISOString(), from: mail.from, auth: mail.auth, nested: mail.nested }, paper, signal);
          if (r.duplicate) n.known += 1;
          else n.sent += 1;
          return;
        } catch (err) {
          const status = err instanceof ApiError ? err.status : 0;
          if (status === 401 || status === 403) throw err;
          if (status === 400) {
            n.refused += 1;
            return;
          }
          if (status === 429 && attempt < 5) await wait(60_000);
          else if (attempt < 1) await wait(3_000);
          else {
            n.failed += 1;
            return;
          }
        }
      }
    };

    // which are in already, asked in pieces the server takes; if it cannot say, everything is sent (kept once anyway)
    const knownOf = async (refs: string[]) => {
      const known = new Set<string>();
      for (let i = 0; i < refs.length; i += KNOWN_MAX) {
        const part = refs.slice(i, i + KNOWN_MAX);
        for (let attempt = 0; attempt < 2; attempt += 1) {
          try {
            for (const r of await api.mailPapersKnown(part)) known.add(r);
            break;
          } catch (err) {
            if (err instanceof ApiError && (err.status === 401 || err.status === 403)) throw err;
            if (attempt === 0) await wait(3_000);
          }
        }
      }
      return known;
    };

    const flush = async (queue: Item[]) => {
      if (queue.length === 0 || stop.current) return;
      const items = queue.splice(0, queue.length);
      const known = await knownOf(items.map((i) => mailRef(i.mail.gmailId, i.paper.index)));
      const todo = items.filter((i) => !known.has(mailRef(i.mail.gmailId, i.paper.index)));
      n.known += items.length - todo.length;
      for (let i = 0; i < todo.length && !stop.current; i += AT_ONCE) {
        await Promise.all(todo.slice(i, i + AT_ONCE).map(sendOne));
        show();
      }
    };

    try {
      const queue: Item[] = [];
      for await (const raw of mailsOf(file, { onRead: (b) => (n.read = b) })) {
        if (stop.current) break;
        n.mails += 1;
        // the day is on the mail's first line: an old mail is passed over without opening it
        if (!raw.receivedAt || raw.receivedAt.getTime() < Date.now() - BACK_MS) {
          n.old += 1;
          show();
          continue;
        }
        let mail: OldMail;
        try {
          mail = await readMail(raw);
        } catch {
          n.refused += 1; // a mail that cannot be taken apart
          continue;
        }
        if (mail.junk) n.junk += 1;
        n.refused += mail.tooBig;
        for (const paper of mail.papers) queue.push({ mail, paper });
        if (queue.length >= BATCH) await flush(queue);
        show();
      }
      await flush(queue);
      setState(stop.current ? 'stopped' : 'done');
    } catch (err) {
      setError(err instanceof NotTakeout ? new Error(t('oldMailNotMbox')) : err);
      setState('stopped');
    }
    show(true);
    if (input.current) input.current.value = '';
    void qc.invalidateQueries({ queryKey: ['papers'] });
  };

  const mb = (b: number) => (b / 1024 / 1024).toFixed(0);
  const fill = (s: string) =>
    s
      .replace('{read}', mb(tally.read))
      .replace('{total}', mb(tally.total))
      .replace('{mails}', String(tally.mails))
      .replace('{sent}', String(tally.sent))
      .replace('{known}', String(tally.known))
      .replace('{old}', String(tally.old))
      .replace('{junk}', String(tally.junk))
      .replace('{refused}', String(tally.refused))
      .replace('{failed}', String(tally.failed));
  const noTrusted = gmail.data && !gmail.data.connection?.trustedDomains.length;

  return (
    <section className="card stack" aria-label={t('oldMailTitle')} data-testid="old-mail-card">
      <h2>{t('oldMailTitle')}</h2>
      <p className="muted">{t('oldMailHint')}</p>
      {noTrusted ? <p className="alert alert-warn">{t('oldMailNoTrusted')}</p> : null}
      {state === 'running' ? (
        <div className="row-actions">
          <span role="status">{fill(t('oldMailRunning'))}</span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => (stop.current = true)}>
            {t('oldMailStop')}
          </button>
        </div>
      ) : (
        <label className="paper-drop">
          <input
            ref={input}
            type="file"
            accept=".mbox,application/mbox"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              if (!/\.mbox$/i.test(f.name)) {
                setError(new Error(t('oldMailNotMbox')));
                e.target.value = '';
                return;
              }
              void run(f);
            }}
          />
          <span>{t('oldMailPick')}</span>
        </label>
      )}
      {state !== 'idle' ? (
        <p className={state === 'done' ? 'alert' : 'muted'} role={state === 'running' ? undefined : 'status'}>
          {state === 'done' ? `${t('oldMailDone')} ` : state === 'stopped' ? `${t('oldMailStopped')} ` : ''}
          {fill(t('oldMailTally'))}
        </p>
      ) : null}
      {error instanceof ApiError ? (
        <ErrorBox error={error} />
      ) : error ? (
        <p className="alert alert-error" role="alert">
          {error instanceof Error ? error.message : String(error)}
        </p>
      ) : null}
    </section>
  );
}
